import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { IndexPageTemplate } from 'modules/templateIndex/types';
import { PrimaryButton, BUTTON_SIZES } from 'ui/button';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { body1 } from 'ui/theme';

import noop from 'utils/noop';
import { SEGMENT_TRACKED_TEMPLATE_ACTION_TRIGGERS } from '../../constants';
import { deleteTemplates } from '../../ducks/actions';

export const content = {
  modalHeaders: { single: 'Delete template?', plural: 'Delete templates?' },
  deleteTemplateButtonText: 'Delete template',
  modalBody: {
    prefix: 'Are you sure you want to delete ',
    suffix: { single: '?', plural: ' templates? This cannot be undone.' },
  },
} as const;

const StyledDiv = styled.div`
  ${body1}
  margin-bottom: 25px;
`;

type DeleteTemplateModalProps = {
  /** Function to Close Modal */
  closeModal: () => void;
  /** refetch */
  refetch: () => void;
  /** a list of templates that are to be deleted */
  templates: IndexPageTemplate[];
  /** String that describes what triggered the deletion dialog to appear. Used for Segment tracking */
  triggeredBy: SEGMENT_TRACKED_TEMPLATE_ACTION_TRIGGERS;
};

export const DeleteTemplateModal = ({
  closeModal = noop,
  refetch = () => {
    // ignore
  },
  templates,
  triggeredBy,
  ...props
}: DeleteTemplateModalProps) => {
  const dispatch = useDispatch();
  const isPlural = templates.length > 1;

  if (templates.length === 0) return <></>;

  return (
    <Modal
      id={`delete-template-modal`}
      isModalOpen={true}
      onClose={closeModal}
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.DELETE}
      data-test={`delete-template-modal`}
      {...props}
    >
      <ModalHeader>
        {isPlural ? content.modalHeaders.plural : content.modalHeaders.single}
      </ModalHeader>
      <ModalBody id="delete-template-modal-body">
        <StyledDiv>
          {content.modalBody.prefix}
          {isPlural ? (
            <>
              {templates.length} {content.modalBody.suffix.plural}
            </>
          ) : (
            <>
              {templates[0].name}
              {content.modalBody.suffix.single}
            </>
          )}
        </StyledDiv>
      </ModalBody>
      <ModalFooter>
        <PrimaryButton
          data-test="delete-template-button"
          onClick={() => {
            const selectedTemplateIds = isPlural
              ? templates.map((tmp) => tmp.id)
              : [templates[0].id];
            dispatch(deleteTemplates(selectedTemplateIds, refetch, triggeredBy));
            closeModal();
          }}
          size={BUTTON_SIZES.LARGE}
        >
          <span>{content.deleteTemplateButtonText}</span>
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
